import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import React, { memo } from 'react'
import { useParams } from 'react-router-dom'
import { useActuators, useSmarthomeModuleState } from '../hooks'
import { BlankView, BlankViewImageVariants } from 'src/Components/blank-view'
import { TimerActuator, SolarActuator as SolarActuatorModel, BatteryActuator as BatteryActuatorModel, ActuatorData } from '../models/smart-home.model'
import SimpleSwitch from './actuators/SimpleSwitch'
import Timer from './actuators/Timer'
import Dimmer from './actuators/Dimmer'
import WindowBlinds from './actuators/WindowBlinds'
import SolarActuator from './actuators/SolarActuator'
import BatteryActuator from './actuators/BatteryActuator'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useFarmContext } from 'src/Elements/Farm'

interface Props {
  hasWritingRights?: boolean
}

const Actuators: React.FC<Props> = () => {
  const { installationId } = useParams<{ installationId: string }>()
  const [actuatorData, actuatorsLoading, actuatorsError, refetchActuators] = useActuators(installationId ?? '')
  const isSmarthomeConfigured = useSmarthomeModuleState(installationId)
  const { isFarm } = useFarmContext()
  const { t } = useCustomTranslation()
  const hasNoActors = (actuatorData ?? []).every(storageActuatorData => storageActuatorData.actors.length === 0)

  const renderActuator = (actuatorData: ActuatorData): React.ReactElement => {
    const actuators = actuatorData.actors.map(actuator => {
      switch (actuator.type) {
        case 72:
          return <SolarActuator serialNo={actuatorData.serialno} actuator={actuator as SolarActuatorModel} />
        case 71:
          return <BatteryActuator serialNo={actuatorData.serialno} actuator={actuator as BatteryActuatorModel} />
        case 70:
          return <Timer serialNo={actuatorData.serialno} actuator={actuator as TimerActuator} />
        case 27:
          return <WindowBlinds serialNo={actuatorData.serialno} actuator={actuator} />
        case 17:
          return <Dimmer serialNo={actuatorData.serialno} actuator={actuator} />
        default:
          return <SimpleSwitch serialNo={actuatorData.serialno} actuator={actuator} />
      }
    })

    return (
      <Stack gap={2}>
        {isFarm && (
          <Typography sx={{ mt: 2, mb: 2 }} variant="h3">
            {t('smart-home.actuator.serialnumber', { serialnumber: actuatorData.serialno })}
          </Typography>
        )}
        {actuators}
      </Stack>
    )
  }

  if (actuatorsLoading) {
    return <CircularProgress />
  }

  if (actuatorsError) {
    return (
      <Box sx={{ p: 2 }}>
        <BlankView
          imageVariant={BlankViewImageVariants.Worried}
          title="smart-home.actuators.connection-error"
          action={{ text: 'smart-home.actuators.connection-error-retry', onClick: refetchActuators }}
        />
      </Box>
    )
  }

  if (isSmarthomeConfigured && hasNoActors) {
    return (
      <Box sx={{ p: 2 }}>
        <BlankView
          imageVariant={BlankViewImageVariants.Worried}
          title="smart-home.blank-page.no-actors.title"
          explanation="smart-home.blank-page.no-actors.description"
        />
      </Box>
    )
  }

  return <>{actuatorData?.map(storageActuatorData => renderActuator(storageActuatorData))}</>
}

export default memo(Actuators)
